import { createContext, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { Id } from '@contracts/common';
import type { BoardDTO, BoardItem, ItemProjectRef } from '@contracts/workplace';
import { ApiClientError } from '../../api/client';
import { onSessionEnd, type MutationOutcome } from '../../api/query';
import { fetchBoard } from './api';
import { changedCards, mergeRemote, releaseDeferred } from './model';

/**
 * The one Workplace board cache. List, Map, Kanban and the realtime stream all read and write
 * `['workplace','board']`; nobody keeps a second copy. There is no polling interval: a read happens on mount and
 * whenever something invalidates the key (a save here, a realtime signal in the stream).
 */
export const BOARD_KEY = ['workplace', 'board'] as const;

/**
 * The project the board is filtered by (`prj`, projects & stakeholders spec Màn 7), or null. The cards drop its code
 * chip, and a new card made on the board starts with it, so it stays on the screen that is being looked at.
 */
export const FilteredProjectContext = createContext<ItemProjectRef | null>(null);

/** Told the cards a server read changed, after it landed (or, for a held-back card, once its gesture ended). */
export type BoardChangeListener = (cards: BoardItem[]) => void;

/**
 * Per-cache state beside the board: cards this person is dragging or typing on (counted per card, since a drag and
 * a typed field can hold one card at once and each lets go on its own), the remote versions held back for
 * them (spec §6.8), whether the next read must skip `since` because the cache may hold a refused write, and who
 * wants to hear which cards a server read changed (the realtime flash, spec §6.8) — `heldChanges` are the busy
 * cards whose change is told only when their gesture ends, together with the row itself.
 */
type BoardSession = {
  busy: Map<Id, number>; pending: Map<Id, BoardItem | null>; fullRead: boolean;
  listeners: Set<BoardChangeListener>; heldChanges: Set<Id>;
};
const sessions = new WeakMap<QueryClient, BoardSession>();

function sessionOf(queryClient: QueryClient): BoardSession {
  let session = sessions.get(queryClient);
  if (session === undefined) {
    session = {
      busy: new Map(), pending: new Map(), fullRead: false, listeners: new Set(), heldChanges: new Set(),
    };
    sessions.set(queryClient, session);
    // A session that ends in this tab takes its held-back rows with it, and the next board read is a whole one:
    // nothing measured against the previous person's cache may be confirmed "unchanged" (authz audit D-F1).
    const ended = session;
    onSessionEnd(queryClient, () => {
      ended.busy.clear();
      ended.pending.clear();
      ended.heldChanges.clear();
      ended.fullRead = true;
    });
  }
  return session;
}

async function readBoard(queryClient: QueryClient, session: BoardSession): Promise<BoardDTO> {
  const cached = session.fullRead ? undefined : queryClient.getQueryData<BoardDTO>(BOARD_KEY);
  session.fullRead = false;
  const answer = await fetchBoard(cached === undefined ? undefined : { revision: cached.revision, reader: cached.reader });
  if ('unchanged' in answer) {
    // Only a read with `since` can be answered "unchanged"; the cache it was measured against is still right.
    if (cached === undefined) throw new ApiClientError(0, 'UNKNOWN', 'Đã có lỗi xảy ra, vui lòng thử lại');
    return cached;
  }
  const local = queryClient.getQueryData<BoardDTO>(BOARD_KEY);
  // A held-back version is only worth keeping while its card is still busy; a fresh remote supersedes the rest.
  for (const id of [...session.pending.keys()]) if (!session.busy.has(id)) session.pending.delete(id);
  if (local !== undefined) tellChanges(session, changedCards(local.items, answer.items));
  if (local === undefined || session.busy.size === 0) return answer;
  const merged = mergeRemote(local, answer, new Set(session.busy.keys()));
  for (const [id, remote] of merged.pending) session.pending.set(id, remote);
  return merged.board;
}

/** Tells the listeners about the changed cards that land now; a busy card's change waits with its row. */
function tellChanges(session: BoardSession, changed: readonly BoardItem[]): void {
  const now: BoardItem[] = [];
  for (const card of changed) {
    if (session.busy.has(card.id)) session.heldChanges.add(card.id); else now.push(card);
  }
  if (now.length === 0) return;
  // After the read has resolved into the cache, so a listener that reads the board sees the new rows.
  queueMicrotask(() => { for (const listener of session.listeners) listener(now); });
}

/** Subscribes to the cards each server read changes in this cache; returns the unsubscribe. */
export function subscribeBoardChanges(queryClient: QueryClient, listener: BoardChangeListener): () => void {
  const session = sessionOf(queryClient);
  session.listeners.add(listener);
  return () => { session.listeners.delete(listener); };
}

/** One optimistic write: how it changes the board now, and the request that makes it true. */
export type BoardMutation<TResult> = {
  apply: (board: BoardDTO) => BoardDTO;
  send: (requestId: Id) => Promise<TResult>;
};

export function useBoard() {
  const queryClient = useQueryClient();
  const session = sessionOf(queryClient);
  const query = useQuery<BoardDTO>({
    queryKey: BOARD_KEY,
    queryFn: () => readBoard(queryClient, session),
    refetchInterval: false,
    // Coming back online is caught up by the reopened stream (`useLive`), the one owner of catch-up reads.
    refetchOnReconnect: false,
  });

  /**
   * Applies `apply` to the cache at once, then sends. A refusal takes the change back: the snapshot is restored when
   * the cache is still exactly what this write left, and otherwise (something else changed it since) the whole
   * board is re-read, because restoring the snapshot would also erase that other change. Either way the request
   * is sent once and the outcome is a value, never a rejection.
   */
  const mutate = useCallback(async <TResult>(m: BoardMutation<TResult>): Promise<MutationOutcome<TResult>> => {
    await queryClient.cancelQueries({ queryKey: BOARD_KEY });
    const snapshot = queryClient.getQueryData<BoardDTO>(BOARD_KEY);
    // What the cache holds after the write (structural sharing may store a copy), to tell later whether it moved.
    const optimistic = snapshot === undefined ? undefined : queryClient.setQueryData(BOARD_KEY, m.apply(snapshot));
    try {
      const result = await m.send(crypto.randomUUID());
      void queryClient.invalidateQueries({ queryKey: BOARD_KEY });
      return { ok: true, result };
    } catch (cause) {
      const error = cause instanceof ApiClientError
        ? cause : new ApiClientError(0, 'UNKNOWN', 'Không thể lưu. Hãy thử lại.');
      if (optimistic !== undefined && queryClient.getQueryData(BOARD_KEY) === optimistic) {
        queryClient.setQueryData(BOARD_KEY, snapshot);
      } else {
        session.fullRead = true;
        void queryClient.invalidateQueries({ queryKey: BOARD_KEY });
      }
      return { ok: false, error };
    }
  }, [queryClient, session]);

  /** A drag or an edit started on `id`: remote changes to that card wait (spec §6.8) until every hold has ended. */
  const markBusy = useCallback((id: Id) => { session.busy.set(id, (session.busy.get(id) ?? 0) + 1); }, [session]);

  /**
   * One gesture on `id` ended. When it was the last hold, the held-back remote version lands, keeping `draft.title`
   * if one is being saved; a release with no hold left does nothing.
   */
  const releaseBusy = useCallback((id: Id, draft?: { title?: string }) => {
    const count = session.busy.get(id);
    if (count === undefined) return;
    if (count > 1) { session.busy.set(id, count - 1); return; }
    session.busy.delete(id);
    const held = session.heldChanges.delete(id);
    const board = queryClient.getQueryData<BoardDTO>(BOARD_KEY);
    if (board === undefined || !session.pending.has(id)) return;
    const remote = session.pending.get(id) ?? null;
    const released = releaseDeferred(board, session.pending, id, draft);
    session.pending.delete(id);
    queryClient.setQueryData(BOARD_KEY, released.board);
    // The held-back change lands now, and its flash with it (spec §6.8).
    if (held && remote !== null) tellChanges(session, [remote]);
  }, [queryClient, session]);

  /**
   * The remote version held back for a busy card (`null`: it was deleted), or `undefined` when nothing waits — so the
   * card modal can say what another person changed while this one types (M1). Read it during render: every read
   * that holds a card back also writes the board, which renders again.
   */
  const heldRemote = useCallback((id: Id): BoardItem | null | undefined => session.pending.get(id), [session]);

  // "Thử lại" on a failed read (UX wave D, M3): the same read again, through the query so its state is told.
  const { refetch } = query;
  const retry = useCallback(() => { void refetch(); }, [refetch]);

  return useMemo(() => ({
    board: query.data, status: query.status, error: query.error, fetching: query.isFetching, retry, mutate, markBusy,
    releaseBusy, heldRemote,
  }), [query.data, query.status, query.error, query.isFetching, retry, mutate, markBusy, releaseBusy, heldRemote]);
}
